// Back-office › Retours : tout ce que les utilisateurs ont dit avec le bouton
// « Un retour ? » (060), le plus récent d'abord.
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { Chip, ErrorBanner, Spinner } from '../components/ui';
import '../feedback/feedback.css';

interface BoFeedback {
  id: string;
  kind: 'incompris' | 'bug' | 'idee' | 'bravo' | 'autre';
  message: string;
  page: string | null;
  voice: boolean;
  createdAt: string;
  organizationName: string | null;
  userName: string | null;
  userEmail: string | null;
}

const KIND: Record<BoFeedback['kind'], string> = {
  incompris: '🤔 Ne comprend pas',
  bug: '🐞 Ne marche pas',
  idee: '💡 Idée',
  bravo: '❤️ Aime',
  autre: 'Retour',
};

const when = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

export function FeedbackTab() {
  const q = useQuery({
    queryKey: ['backoffice', 'feedback'] as const,
    queryFn: () => api.get<{ feedback: BoFeedback[] }>('/api/backoffice/feedback'),
    staleTime: 0,
    retry: false,
  });
  if (q.isPending) return <Spinner label="Chargement des retours" />;
  if (q.isError) return <ErrorBanner>{q.error instanceof Error ? q.error.message : 'Lecture impossible.'}</ErrorBanner>;
  const list = q.data.feedback;
  return (
    <section className="zy-card bo-section">
      <div className="bo-section-head">
        <h2 className="t-h3">Retours des utilisateurs</h2>
        <span className="bo-meta">
          {list.length} retour{list.length > 1 ? 's' : ''} · chacun arrive aussi par e-mail.
        </span>
      </div>
      {list.length === 0 ? (
        <p className="bo-meta">Aucun retour pour l’instant. Le bouton « Un retour ? » est en bas à droite de chaque écran.</p>
      ) : (
        <ul className="bo-feedback">
          {list.map((f) => (
            <li key={f.id}>
              <div className="bo-feedback-head">
                <Chip>{KIND[f.kind] ?? KIND.autre}</Chip>
                {f.voice ? <Chip title="Dicté à la voix">🎙️ voix</Chip> : null}
                <strong>{f.userName ?? 'Utilisateur'}</strong>
                {f.userEmail ? <a href={`mailto:${f.userEmail}`}>{f.userEmail}</a> : null}
                {f.organizationName ? <span className="bo-meta">· {f.organizationName}</span> : null}
                <span className="bo-meta bo-feedback-when">{when(f.createdAt)}</span>
              </div>
              <p className="bo-feedback-text">{f.message}</p>
              {f.page ? <span className="bo-meta">Page : {f.page}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
